import {useCallback, useEffect} from '@wordpress/element';
import {motion, AnimatePresence} from 'framer-motion';
import {useToastDispatch, useToasts} from '../../store/toast';

const TYPE_STYLES = {
    success: {bar: 'bg-emerald-500', icon: '✓', iconClass: 'bg-emerald-100 text-emerald-600'},
    warning: {bar: 'bg-amber-400', icon: '⚠', iconClass: 'bg-amber-100 text-amber-600'},
    error: {bar: 'bg-red-500', icon: '✕', iconClass: 'bg-red-100 text-red-600'},
    info: {bar: 'bg-blue-500', icon: 'i', iconClass: 'bg-blue-100 text-blue-600'},
};

function Toast({t, onClose}) {
    const style = TYPE_STYLES[t.type] || TYPE_STYLES.info;

    useEffect(() => {
        if (!t.duration) return;
        const timer = setTimeout(() => onClose(t.id), t.duration);
        return () => clearTimeout(timer);
    }, [t.id, t.duration, onClose]);

    return (
        <motion.div
            initial={{opacity: 0, x: 80, scale: 0.95}}
            animate={{opacity: 1, x: 0, scale: 1}}
            exit={{opacity: 0, x: 80, scale: 0.95}}
            transition={{type: 'spring', stiffness: 400, damping: 30}}
            className="relative flex items-start gap-3 bg-base-100 shadow-xl rounded-xl px-4 py-3 w-[320px] overflow-hidden"
        >
            <div className={`absolute left-0 inset-y-0 w-1 rounded-l-xl ${style.bar}`}/>

            <div
                className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-black shrink-0 mt-0.5 ${style.iconClass}`}>
                {style.icon}
            </div>

            <div className="flex-1 min-w-0">
                {t.title && <p className="font-semibold text-sm leading-tight">{t.title}</p>}
                {t.message && <p className="text-xs text-base-content/70 leading-snug break-words">{t.message}</p>}
            </div>

            <button
                className="shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-base-content/40 hover:text-base-content hover:bg-base-200 transition-colors text-xs mt-0.5"
                onClick={() => onClose(t.id)}
            >
                ✕
            </button>

            {t.duration && (
                <motion.div
                    className={`absolute bottom-0 left-0 h-0.5 ${style.bar} opacity-40`}
                    initial={{width: '100%'}}
                    animate={{width: '0%'}}
                    transition={{duration: t.duration / 1000, ease: 'linear'}}
                />
            )}
        </motion.div>
    );
}

export function ToastHost() {
    const toasts = useToasts();
    const {remove} = useToastDispatch();

    const onClose = useCallback((id) => remove(id), [remove]);

    return (
        <div className="fixed bottom-4 right-4 z-[1000] flex flex-col gap-2 items-end">
            <AnimatePresence initial={false}>
                {Array.isArray(toasts) && toasts.map((t) => (
                    <Toast key={t.id} t={t} onClose={onClose}/>
                ))}
            </AnimatePresence>
        </div>
    );
}